import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  TextField, 
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import axiosInstance from '../api/axiosConfig';
import ConfirmDialog from './ConfirmDialog';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const PlaylistList: React.FC = () => {
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; pid: string | null }>({ 
    open: false, 
    pid: null 
  });
  const theme = useTheme();

  const fetchPlaylists = async () => {
    try {
      const resp = await axiosInstance.get(`${backendUrl}/api/playlists`);
      setPlaylists(resp.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { fetchPlaylists(); }, []);

  const create = async () => {
    if (!name) return;
    try {
      await axiosInstance.post(`${backendUrl}/api/playlists`, { name, description });
      setName(''); setDescription('');
      fetchPlaylists();
    } catch (e) { console.error(e); }
  };

  const del = async (pid: string) => {
    setConfirmDialog({ open: true, pid });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDialog.pid) return;
    
    try {
      await axiosInstance.delete(`${backendUrl}/api/playlists/${confirmDialog.pid}`);
      fetchPlaylists();
    } catch (e) {
      console.error(e);
    } finally {
      setConfirmDialog({ open: false, pid: null });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialog({ open: false, pid: null });
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>My Playlists</Typography>
        
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          p: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <TextField 
            size="small" 
            placeholder="New Playlist Name" 
            value={name} 
            onChange={(e) => setName(e.target.value)} 
            sx={{ flexGrow: 1, maxWidth: 300 }}
          />
          <TextField 
            size="small" 
            placeholder="Description (optional)" 
            value={description} 
            onChange={(e) => setDescription(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button 
            variant="contained" 
            onClick={create}
            startIcon={<AddIcon />}
            disabled={!name}
            sx={{ borderRadius: 2 }}
          >
            Create
          </Button>
        </Box>
      </Box>

      <Box display="grid" sx={{ 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, 
        gap: 3 
      }}>
        {playlists.map((p) => (
          <Card 
            key={p.pid}
            elevation={0}
            sx={{ 
              height: '100%',
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.05)',
              bgcolor: 'background.paper',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                borderColor: 'primary.main'
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {p.name}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => del(p.pid)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                {p.description || "No description"}
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="caption" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', px: 1, py: 0.5, borderRadius: 1 }}>
                  {p.counter || (p.videos && p.videos.length) || 0} videos
                </Typography>
                <Button 
                  component={Link} 
                  to={`/playlists/${p.pid}`} 
                  variant="outlined" 
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Open
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {playlists.length === 0 && (
        <Typography align="center" sx={{ mt: 8, color: 'text.secondary' }}>
          No playlists found. Create one above!
        </Typography>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Playlist"
        message="Are you sure you want to delete this playlist? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Container>
  );
};

export default PlaylistList;
